import Modal from "/WebInterface/new-ui/modules/modal.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import fileArrangementTemplate, {
  arrangementCardTemplate,
} from "./template.js";
import store from "/WebInterface/new-ui/store/index.js";
import { defaultFileListingColumnsOrder } from "/WebInterface/new-ui/assets/js/constants.js";

const STORAGE_KEY = "file-listing-table-columns-arrangement";

function getStoredArrangement() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getColumnOrder(useDefault = false) {
  const stored = useDefault ? null : getStoredArrangement();
  const base = stored || defaultFileListingColumnsOrder;
  const entries = Object.entries(base).sort(
    (a, b) => (a[1].order ?? 999) - (b[1].order ?? 999)
  );
  return entries.map(([key]) => key);
}

function renderCards(wrapper, columnOrder) {
  wrapper.html("");
  columnOrder.forEach((columnName) => {
    wrapper.el().insertAdjacentHTML(
      "beforeend",
      arrangementCardTemplate({
        transfer_value: columnName,
        label: t(columnName),
        i18n_key: columnName,
      })
    );
  });
}

function bindReorderDragDrop(wrapper) {
  const cards = wrapper.find(".arrangement-card").sel();
  if (cards.length === 0) return;

  cards.forEach((card) => {
    card.setAttribute("draggable", "true");
    card.addEventListener("dragstart", onDragStart);
    card.addEventListener("dragover", onDragOver);
    card.addEventListener("dragleave", onDragLeave);
    card.addEventListener("drop", onDrop);
    card.addEventListener("dragend", onDragEnd);
  });

  let draggedEl = null;

  function onDragStart(e) {
    draggedEl = e.currentTarget;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(
      "text/plain",
      _$(e.currentTarget).attr("data-transfer-value")
    );
    e.currentTarget.classList.add("arrangement-card-dragging");
  }

  function onDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const target = e.currentTarget;
    if (target !== draggedEl) target.classList.add("dragged-on");
  }

  function onDragLeave(e) {
    e.currentTarget.classList.remove("dragged-on");
  }

  function onDrop(e) {
    e.preventDefault();
    e.currentTarget.classList.remove("dragged-on");
    const target = e.currentTarget;
    if (!draggedEl || target === draggedEl) return;
    const parent = target.parentNode;
    const all = Array.from(parent.querySelectorAll(".arrangement-card"));
    const dragIdx = all.indexOf(draggedEl);
    const targetIdx = all.indexOf(target);
    if (dragIdx === -1 || targetIdx === -1) return;
    if (dragIdx < targetIdx) {
      target.insertAdjacentElement("afterend", draggedEl);
    } else {
      target.insertAdjacentElement("beforebegin", draggedEl);
    }
  }

  function onDragEnd(e) {
    e.currentTarget.classList.remove("arrangement-card-dragging");
    wrapper
      .find(".arrangement-card")
      .sel()
      .forEach((el) => {
        el.classList.remove("dragged-on");
      });
    draggedEl = null;
  }
}

export const initListingColumnArrangement = () => {
  const wrapper = _$("#listingTableArrangement .table-column-arrangement");
  if (!wrapper.el()) return;

  const render = (setDefault = false) => {
    const columnOrder = getColumnOrder(setDefault);
    renderCards(wrapper, columnOrder);
    bindReorderDragDrop(wrapper);
  };

  new Modal({
    title: "arrangeFileListingTableColumns",
    body: fileArrangementTemplate({}),
    uniqueId: "column-arrangement-modal",
    onBeforeShow: () => {
      _$("html").addClass("file-listing-arrangement-open");
    },
    onAfterHide: () => {
      _$("html").removeClass("file-listing-arrangement-open");
    },
    buttons: [
      {
        label: "setToDefault",
        close: false,
        classes: ["secondary-button", "mr-3"],
        onClick: () => render(true),
      },
      {
        label: "save",
        close: true,
        classes: ["primary-button", "mr-3"],
        onClick: () => {
          const cards = _$("#listingTableArrangement .arrangement-card").sel();
          const stored = getStoredArrangement() || {
            ...defaultFileListingColumnsOrder,
          };
          cards.forEach((card, index) => {
            const columnName = _$(card).attr("data-transfer-value");
            if (stored[columnName]) {
              stored[columnName] = { ...stored[columnName], order: index };
            }
          });
          localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
          store.dispatch("setFileListing", store.files);
        },
      },
    ],
  });

  render();
};
